import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faChevronRight, faTimes } from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { FixtureOverviewTitle } from '#src/components/fixture/FixtureTeamsOverview/FixtureOverview/FixtureOverviewTitle/index.tsx';
import { NeonRewards } from '#src/components/rewards/NeonRewards/index.tsx';

import type {
  RewardConfigsOverviewHeader_anyRewardConfigInterface,
  RewardConfigsOverviewHeader_so5Fixture,
} from './__generated__/index.graphql.ts';

const StyledIconButton = styled(IconButton)`
  margin-left: auto;
`;

const StyledSBHorizontal = styled(SBHorizontal)`
  &.fit {
    width: 100%;
  }
`;

type Props = {
  so5Fixture?: RewardConfigsOverviewHeader_so5Fixture;
  multipliedRewardConfigs: RewardConfigsOverviewHeader_anyRewardConfigInterface[];
  onlyRewards?: boolean;
  verificationNeeded?: { phoneNumber: boolean; kyc: boolean };
  onClose?: () => void;
  myCompletedThresholdsStreakTasksCount?: number;
  myCommonCompletedThresholdsStreakTasksCount?: number;
};
export const RewardConfigsOverviewHeader = ({
  so5Fixture,
  multipliedRewardConfigs,
  onlyRewards,
  verificationNeeded,
  onClose,
  myCompletedThresholdsStreakTasksCount,
  myCommonCompletedThresholdsStreakTasksCount,
}: Props) => {
  const {
    flags: { useKillRewardTab = false },
  } = useFeatureFlags();
  const { generateSportContextPath } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();

  const cashRewardConfigs = multipliedRewardConfigs.filter(
    isTypeFn('MonetaryRewardConfig')
  );
  const boxRewardConfigs = multipliedRewardConfigs.filter(
    isTypeFn('ProbabilisticBundleRewardConfig')
  );
  const cardRewardConfigs = multipliedRewardConfigs.filter(
    isTypeFn('CardRewardConfig')
  );
  const shardRewardConfigs = multipliedRewardConfigs.filter(
    isTypeFn('CardShardRewardConfig')
  );

  const mainRewardsPresent =
    shardRewardConfigs.length > 0 ||
    cardRewardConfigs.length > 0 ||
    cashRewardConfigs.length > 0 ||
    boxRewardConfigs.length > 0;

  return (
    <Vertical gap={3}>
      {!isLaptopAndAbove && !!onClose && (
        <StyledIconButton
          icon={faTimes}
          color="transparent"
          size="compact"
          onClick={onClose}
        />
      )}
      {!onlyRewards && !!so5Fixture && (
        <Vertical center gap={3}>
          <StyledSBHorizontal className={classnames({ fit: useKillRewardTab })}>
            <StarBall width={40} height={40} />
            {!!useKillRewardTab && (
              <Button
                size="small"
                color="transparent"
                to={generateSportContextPath(
                  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS,
                  {
                    params: {
                      fixture: so5Fixture.slug,
                      eventType: so5Fixture.eventType.toLowerCase(),
                    },
                  }
                )}
                endIcon={<FontAwesomeIcon icon={faChevronRight} />}
              >
                <FormattedMessage {...fantasy.viewRewards} />
              </Button>
            )}
          </StyledSBHorizontal>
          <BrandS color="var(--c-nd-600)">
            <FixtureOverviewTitle so5Fixture={so5Fixture} />
          </BrandS>
        </Vertical>
      )}
      {!!mainRewardsPresent && (
        <NeonRewards
          verificationNeeded={verificationNeeded}
          anyRewardConfigInterfaces={multipliedRewardConfigs}
          myCompletedThresholdsStreakTasksCount={
            myCompletedThresholdsStreakTasksCount
          }
          myCommonCompletedThresholdsStreakTasksCount={
            myCommonCompletedThresholdsStreakTasksCount
          }
          size={32}
        />
      )}
    </Vertical>
  );
};

RewardConfigsOverviewHeader.fragments = {
  so5Fixture: gql`
    fragment RewardConfigsOverviewHeader_so5Fixture on So5Fixture {
      slug
      id
      eventType
      ...FixtureOverviewTitle_so5Fixture
    }
    ${FixtureOverviewTitle.fragments.so5Fixture}
  ` as TypedDocumentNode<RewardConfigsOverviewHeader_so5Fixture>,
  anyRewardConfigInterface: gql`
    fragment RewardConfigsOverviewHeader_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...NeonRewards_anyRewardConfigInterface
    }
    ${NeonRewards.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardConfigsOverviewHeader_anyRewardConfigInterface>,
};
